import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import * as Auth from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { HubCapsule } from 'aws-amplify-react-native/types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProtectedRoute from '@/hooks/useProtectedRoute';

export interface CognitoUserInterface {
  signInDetails?: {
    authFlowType?: string;
    loginId?: string;
  };
  userId?: string;
  username?: string;
}

type AuthContextProps = {
  cognitoUser?: CognitoUserInterface;
  jwt?: string;
  payload?: { [k: string]: any };
  userIsLoading: boolean;
};

const AuthContext = createContext<AuthContextProps>({
  cognitoUser: undefined,
  jwt: undefined,
  payload: undefined,
  userIsLoading: true,
});

const AuthProvider: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const [cognitoUser, setCognitoUser] = useState<CognitoUserInterface | undefined>();
  const [jwt, setJwt] = useState<string | undefined>();
  const [payload, setPayload] = useState<{ [k: string]: any } | undefined>();
  const [loading, setLoading] = useState(true);

  useProtectedRoute(jwt, loading);

  const currentUser = async () => {
    try {
      setLoading(true);
      const authUser = await Auth.getCurrentUser();
      const authSession = await Auth.fetchAuthSession();
      setJwt(authSession.tokens?.idToken?.toString());
      setPayload(authSession.tokens?.idToken?.payload);
      setCognitoUser(authUser);

      const stayConnected = await AsyncStorage.getItem('stayConnected');
      if (stayConnected === 'true') {
        await Auth.rememberDevice();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setCognitoUser(undefined);
      setJwt(undefined);
      setPayload(undefined);
    }
  };

  const authListener = ({ payload: { event } }: HubCapsule) => {
    switch (event) {
      case 'signedIn':
        currentUser();
        break;
      case 'signedOut':
        setLoading(true);
        setCognitoUser(undefined);
        setJwt(undefined);
        setPayload(undefined);
        setLoading(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    currentUser();
    const listener = Hub.listen('auth', authListener);
    return () => listener();
  }, []);

  const values = useMemo(() => {
    return {
      cognitoUser,
      jwt,
      userIsLoading: loading,
      payload,
    };
  }, [jwt, loading, JSON.stringify(cognitoUser)]);

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

const useAuthContext = () => useContext(AuthContext);

export { useAuthContext, AuthContext, AuthProvider };
